import React, { useState, useMemo, useEffect } from 'react';
import { useDebounce } from 'react-use';
import {
	Box, Flex, Text, Input, Table, Thead, Tbody, Tr, Th, Td, Icon, Button,
} from '@chakra-ui/react';
import { FontAwesomeIcon as Fa } from '@fortawesome/react-fontawesome';
import {
	faCaretUp, faCaretDown, faAngleDoubleLeft, faAngleDoubleRight, faAngleLeft, faAngleRight,
} from '@fortawesome/free-solid-svg-icons';
import { useTable, useSortBy } from 'react-table';
import dayjs from 'dayjs';
import useSWR from 'swr';
import {
	Paginator, Previous, Next, PageGroup, usePaginator,
} from '../Paginator';
import GraphContainer from './Container';

export default function Dataset({
	table, data: _data, shortName, bigTitle,
}) {
	const data = useMemo(() => _data ?? [], [_data]);
	const originalColumns = table?.schema.map((s) => s.path);
	const fullColumns = originalColumns.filter((t) => data.every((d) => d[t] !== undefined));

	const columns = useMemo(() => table?.schema.filter((t) => fullColumns.findIndex((f) => t.path === f) >= 0).map((s) => ({
		Header: s.path,
		accessor: s.path,
		isNumeric: s.instance === 'Number',
		Cell: ({ row }) => {
			if (s.instance === 'Number' && !s.path.includes('年') && !row.original[s.path]?.toString()?.includes('.')) {
				return row.original[s.path]?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',') || '';
			}
			if (s.instance === 'Date') {
				return dayjs(row.original[s.path]).format('YYYY/MM/DD');
			}
			if (Array.isArray(row.original[s.path])) {
				return row.original[s.path].join('、');
			}
			return row.original[s.path] || '';
		},
	})), [table]);

	const [totalRecordNum] = useState(data.length);
	const [searchTerm, setSearchTerm] = useState('');
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
	const [hitRecordNum, setHitRecordNum] = useState(0);
	const [hitMarkers, setHitMarkers] = useState([]);

	useDebounce(() => {
		setDebouncedSearchTerm(searchTerm);
	}, 500, [searchTerm]);

	useEffect(() => {
		const m = data.map((marker) => {
			const x = marker;
			delete x.red;
			if (!searchTerm.length) {
				return x;
			}
			Object.values(x).forEach((obj) => {
				if (String(obj)?.includes(searchTerm)) {
					x.red = true;
				}
			});
			return x;
		});
		if (searchTerm !== '') {
			setHitMarkers(m.filter((word) => word.red === true));
		} else {
			setHitMarkers(data.map((x) => ({
				...x,
				red: false,
			})) ?? []);
		}
		setHitRecordNum(m.filter((word) => word.red === true).length);
	}, [debouncedSearchTerm]);

	const {
		getTableProps, getTableBodyProps, headerGroups, rows, prepareRow,
	} = useTable({
		columns: columns || [],
		data: hitMarkers || [],
	}, useSortBy);

	const recordPerPage = 50;
	const [pagesQuantity, setPagesQuantity] = useState(Math.ceil(hitMarkers.length / recordPerPage));
	const { currentPage, setCurrentPage } = usePaginator({
		initialState: { currentPage: 1, pageSize: 50 },
	});
	const { data: graphMeta } = useSWR(`/api/v1/graphs/${shortName}`);
	const datasetAltText = `這張圖是說明關於${graphMeta?.name || shortName || '圖表'}的統計狀況`;

	useEffect(() => {
		setPagesQuantity(Math.ceil(hitMarkers.length / recordPerPage));
	}, [hitMarkers]);

	return (
		<GraphContainer shortName={shortName} bigTitle={bigTitle}>
			<Flex>
				<Box shadow="md" borderRadius={10} m={2} w="100%">
					<Flex justifyContent="space-between">
						<Box display="flex" alignItems="center">
							<Text ml={3} color="subtitle" fontSize="sm">
								全部
								{' '}
								{totalRecordNum}
								{' '}
								筆
								{ debouncedSearchTerm !== '' ? (
									`，符合「${debouncedSearchTerm}」共有 ${hitRecordNum} 筆`
								) : null }
							</Text>
						</Box>
						<Input
							type="search"
							aria-label={`搜尋${graphMeta?.name ?? '資料清冊'}`}
							title={`搜尋${graphMeta?.name ?? '資料清冊'}`}
							width={250}
							placeholder={`搜尋${graphMeta?.name ?? ''}...`}
							value={searchTerm}
							onChange={(e) => {
								setSearchTerm(e.target.value.trim());
								setCurrentPage(1);
							}}
							// position="sticky"
							// top={0}
							bgColor="background"
							color="subtitle"
							// zIndex={99}
							boxShadow="1px 1px 3px #333"
							variant="unstyled"
							fontSize="xs"
							px={2}
						/>
					</Flex>
					<Box overflowX="auto">
						<Table {...getTableProps()} size="sm" w="98%" mx="auto" mt={3} aria-label={datasetAltText}>
							<Thead>
								{headerGroups.map((headerGroup) => (
									<Tr key={0} {...headerGroup.getHeaderGroupProps()}>
										{headerGroup.headers.map((column) => (
											<Th
												scope="col"
												key={0}
												{...column.getHeaderProps()}
												isNumeric={column.isNumeric}
												color="rgb(153, 153, 153)"
												aria-sort={column.isSorted ? (column.isSortedDesc ? 'descending' : 'ascending') : 'none'}
											>
												<Box
													as="button"
													type="button"
													{...column.getSortByToggleProps({ title: '點此排序' })}
													display="inline-flex"
													alignItems="center"
													color="inherit"
													bgColor="transparent"
													border="0"
													p={0}
													m={0}
													cursor="pointer"
													fontWeight="inherit"
													fontSize="inherit"
													lineHeight="inherit"
												>
													{column.render('Header')}
													{column.isSorted ? (
														column.isSortedDesc ? (
															<Icon as={Fa} ml={1} icon={faCaretDown} aria-label="降冪排序" />
														) : (
															<Icon as={Fa} ml={1} icon={faCaretUp} aria-label="升冪排序" />
														)
													) : null}
												</Box>
											</Th>
										))}
									</Tr>
								))}
							</Thead>
							<Tbody {...getTableBodyProps()}>
								{rows.map((row, index) => {
									prepareRow(row);
									return (
										<Tr key={0} {...row.getRowProps()} className="tr">
											{ (index >= recordPerPage * (currentPage - 1)
												&& index <= recordPerPage * currentPage - 1)
												&& (row.original.red || debouncedSearchTerm === '') ? (
													<>
														{row.cells.map((cell) => (
															<Td
																key={0}
																{...cell.getCellProps()}
																isNumeric={cell.column.isNumeric}
																title={cell.column.Header?.toString?.() || ''}
															>
																{cell.render('Cell')}
															</Td>
														))}
													</>
												) : null}
										</Tr>
									);
								})}
							</Tbody>
						</Table>
					</Box>
				</Box>
			</Flex>
			<Box maxW="container.xl">
				<Paginator
					pagesQuantity={pagesQuantity}
					currentPage={currentPage}
					onPageChange={setCurrentPage}
					innerLimit={2}
					outerLimit={2}
					normalStyles={{
						w: 7,
						bgColor: 'transparent',
						fontSize: 'sm',
						color: 'secDark',
					}}
					activeStyles={{
						w: 7,
						bgColor: 'transparent',
						fontSize: 'sm',
						borderRadius: 0,
						borderBottom: '2px solid #777',
						fontWeight: 800,
						color: 'pryPurple',
					}}
				>
					<Flex align="center" justify="center" p={4}>
						<Button aria-label="第一頁" bgColor="transparent" disabled={currentPage === 1} onClick={() => setCurrentPage(1)} minW={1} w={7}>
							<Icon as={Fa} icon={faAngleDoubleLeft} />
						</Button>
						<Previous aria-label="上一頁" bgColor="transparent" minW={1} w={7}>
							<Icon as={Fa} icon={faAngleLeft} />
						</Previous>
						<PageGroup align="center" />
						<Next aria-label="下一頁" bgColor="transparent" minW={1} w={7}>
							<Icon as={Fa} icon={faAngleRight} />
						</Next>
						<Button aria-label="最後一頁" bgColor="transparent" disabled={currentPage === pagesQuantity} onClick={() => setCurrentPage(pagesQuantity)} minW={1} w={7}>
							<Icon as={Fa} icon={faAngleDoubleRight} />
						</Button>
					</Flex>
				</Paginator>
			</Box>
		</GraphContainer>
	);
}
